import { PAGE_BG_STYLE } from "@/lib/pageBg";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { UserBadges, BadgeDisplay } from "@/components/UserBadges";
import { useBadges } from "@/hooks/useBadges";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { 
  User, Image, TrendingUp, Heart, Calendar, 
  ExternalLink, Sparkles, Crown, Star, Award, DollarSign, Coins
} from "lucide-react";
import { useEmonRate } from "@/contexts/EmonPriceContext";
import { priceToUsd } from "@/lib/priceUtils";
import { formatDistanceToNow } from "date-fns";
import { toast } from "@/hooks/use-toast";


interface ProfileData {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  wallet_address: string;
  created_at: string;
}

interface NFTData {
  id: string;
  name: string;
  image_url: string | null;
  current_price: number | null;
  price: number;
  sales_count: number;
  likes_count: number;
  is_listed: boolean | null;
}

interface Stats {
  totalNFTsCreated: number;
  totalNFTsOwned: number;
  totalSales: number;
  totalLikes: number;
  totalEarned: number;
}

export default function PublicProfile() {
  const { identifier } = useParams<{ identifier: string }>();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [createdNfts, setCreatedNfts] = useState<NFTData[]>([]);
  const [ownedNfts, setOwnedNfts] = useState<NFTData[]>([]);
  const [stats, setStats] = useState<Stats>({ totalNFTsCreated: 0, totalNFTsOwned: 0, totalSales: 0, totalLikes: 0, totalEarned: 0 });
  const [loading, setLoading] = useState(true);
  const [isEarlyCreator, setIsEarlyCreator] = useState(false);
  const [activeTab, setActiveTab] = useState<"created" | "owned">("created");
  const [selectedNft, setSelectedNft] = useState<NFTData | null>(null);
  
  // Determine if identifier is UUID or wallet address
  const isUUID = identifier ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier) : false;
  
  const { badges } = useBadges(profile?.wallet_address);

  useEffect(() => {
    if (identifier) {
      fetchProfileData(identifier, isUUID);
    }
  }, [identifier, isUUID]);

  const fetchProfileData = async (id: string, byUUID: boolean) => {
    setLoading(true);
    try {
      // Fetch profile by UUID or wallet address
      let profileData;
      if (byUUID) {
        const { data } = await supabase
          .from("profiles")
          .select("id, display_name, avatar_url, wallet_address, created_at")
          .eq("id", id)
          .single();
        profileData = data;
      } else {
        const { data } = await supabase
          .from("profiles")
          .select("id, display_name, avatar_url, wallet_address, created_at")
          .eq("wallet_address", id)
          .single();
        profileData = data;
      }

      if (profileData) {
        setProfile(profileData);
      } else {
        // Profile not found
        setProfile(null);
        setLoading(false);
        return;
      }

      const walletAddress = profileData.wallet_address;

      // Fetch ALL NFTs owned by this wallet (including created ones that weren't sold)
      const { data: ownedNftData } = await supabase
        .from("nfts")
        .select("id, name, image_url, current_price, price, sales_count, likes_count, is_listed, total_earned, creator_wallet")
        .eq("owner_wallet", walletAddress)
        .order("created_at", { ascending: false });

      setOwnedNfts(ownedNftData || []);

      // Fetch NFTs created by this wallet
      const { data: createdNftData } = await supabase
        .from("nfts")
        .select("id, name, image_url, current_price, price, sales_count, likes_count, is_listed, total_earned, creator_wallet")
        .eq("creator_wallet", walletAddress)
        .order("created_at", { ascending: false });

      setCreatedNfts(createdNftData || []);

      // Fetch actual sales made by this wallet from ownership history
      const { data: salesMade } = await supabase
        .from("nft_ownership_history")
        .select("id, price")
        .eq("from_wallet", walletAddress)
        .eq("transaction_type", "sale");

      // Calculate stats from created NFTs (creator earns from sales)
      const allNfts = [...(createdNftData || []), ...(ownedNftData || [])];
      const totalLikes = allNfts.reduce((sum, nft) => sum + (nft.likes_count || 0), 0);
      const totalSales = salesMade?.length || 0;
      const totalEarned = (createdNftData || []).reduce((sum, nft) => sum + ((nft as any).total_earned || 0), 0);
      
      setStats({
        totalNFTsCreated: createdNftData?.length || 0,
        totalNFTsOwned: ownedNftData?.length || 0,
        totalSales,
        totalLikes,
        totalEarned
      });

      // Check if early creator
      const { data: earlyCreator } = await supabase
        .from("early_creator_claims")
        .select("id")
        .eq("wallet_address", walletAddress)
        .single();

      setIsEarlyCreator(!!earlyCreator);

    } catch (error) {
      console.error("Error fetching profile:", error);
    }
    setLoading(false);
  };

  const displayName = profile?.display_name || "Creator";


  if (loading) {
    return (
      <div className="min-h-screen relative" style={PAGE_BG_STYLE}>
        <AppHeader />
        <main className="container max-w-7xl mx-auto px-4 pt-20 pb-24">
          <div className="space-y-8">
            <Skeleton className="h-64 w-full rounded-3xl" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={PAGE_BG_STYLE}>
      <AppHeader />
      
      <main className="container max-w-7xl mx-auto px-4 pt-20 pb-24">

        {/* Hero Profile Section */}
        <div className="relative mb-8">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/10 to-background rounded-3xl blur-3xl opacity-50" />
          
          <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-card/80 via-card to-card/90 backdrop-blur-xl rounded-3xl">
            {/* Animated background pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,hsl(var(--primary)/0.15),transparent_40%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,hsl(var(--accent)/0.1),transparent_40%)]" />
            
            {isEarlyCreator && (
              <div className="absolute top-4 right-4 z-10">
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 px-3 py-1 text-sm font-bold animate-pulse">
                  <Star className="w-4 h-4 mr-1" />
                  Early Creator
                </Badge>
              </div>
            )}
            
            <CardContent className="relative p-6 md:p-10">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
                {/* Avatar */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent rounded-full blur-xl opacity-50 animate-pulse" />
                  <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-primary/30 bg-gradient-to-br from-primary/20 to-accent/20">
                    {profile?.avatar_url ? (
                      <img 
                        src={profile.avatar_url} 
                        alt={displayName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-16 h-16 md:w-20 md:h-20 text-primary/50" />
                      </div>
                    )}
                  </div>
                  {isEarlyCreator && (
                    <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full p-2 shadow-lg">
                      <Crown className="w-6 h-6 text-white" />
                    </div>
                  )}
                </div>

                {/* Profile Info */}
                <div className="flex-1 text-center md:text-left space-y-4">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2 flex items-center justify-center md:justify-start gap-2">
                      {displayName}
                      {isEarlyCreator && <Sparkles className="w-6 h-6 text-amber-400" />}
                    </h1>
                    
                    {/* Member badge instead of wallet */}
                    <div className="inline-flex items-center gap-2 text-muted-foreground">
                      <User className="w-4 h-4" />
                      <span className="text-sm">FLOW Creator</span>
                    </div>
                  </div>

                  {/* Badges */}
                  {badges.length > 0 && (
                    <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                      <BadgeDisplay badges={badges} />
                    </div>
                  )}


                  {/* Member since */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground justify-center md:justify-start">
                    <Calendar className="w-4 h-4" />
                    <span>Member since {profile?.created_at ? formatDistanceToNow(new Date(profile.created_at), { addSuffix: true }) : "recently"}</span>
                  </div>

                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={<Image className="w-6 h-6" />}
            label="NFTs Created"
            value={stats.totalNFTsCreated}
            gradient="from-blue-500/20 to-cyan-500/20"
          />
          <StatCard 
            icon={<TrendingUp className="w-6 h-6" />}
            label="Total Sales"
            value={stats.totalSales}
            gradient="from-green-500/20 to-emerald-500/20"
          />
          <StatCard 
            icon={<Heart className="w-6 h-6" />}
            label="Total Likes"
            value={stats.totalLikes}
            gradient="from-pink-500/20 to-rose-500/20"
          />
          <StatCard 
            icon={<DollarSign className="w-6 h-6" />}
            label="USD Earned"
            value={`$${stats.totalEarned.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            gradient="from-purple-500/20 to-violet-500/20"
          />
        </div>

        {/* NFT Gallery */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Image className="w-6 h-6 text-primary" />
              NFT Collection
            </h2>
          </div>

          {/* Tabs for Created / Owned */}
          <div className="flex gap-2 border-b border-border/50">
            <button
              onClick={() => setActiveTab("created")}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
                activeTab === "created" 
                  ? "border-primary text-primary" 
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Created ({stats.totalNFTsCreated})
            </button>
            <button
              onClick={() => setActiveTab("owned")}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
                activeTab === "owned" 
                  ? "border-primary text-primary" 
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Owned ({stats.totalNFTsOwned})
            </button>
          </div>

          {activeTab === "created" ? (
            createdNfts.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No NFTs created yet</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {createdNfts.map((nft) => (
                  <ProfileNFTCard key={nft.id} nft={nft} onOpen={() => setSelectedNft(nft)} />
                ))}
              </div>
            )
          ) : (
            ownedNfts.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No NFTs owned yet</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {ownedNfts.map((nft) => (
                  <ProfileNFTCard key={nft.id} nft={nft} onOpen={() => setSelectedNft(nft)} />
                ))}
              </div>
            )
          )}
        </div>

        <Dialog
          open={!!selectedNft}
          onOpenChange={(open) => {
            if (!open) setSelectedNft(null);
          }}
        >
          <DialogContent className="sm:max-w-3xl">
            {selectedNft && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-primary">{selectedNft.name}</DialogTitle>
                  <DialogDescription>
                    {selectedNft.is_listed === false
                      ? "This NFT is currently not for sale."
                      : "View details and open it in the marketplace."}
                  </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="aspect-square overflow-hidden rounded-xl bg-muted">
                    {selectedNft.image_url ? (
                      <img
                        src={selectedNft.image_url}
                        alt={`${selectedNft.name} NFT image`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-16 h-16 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-xl border border-border/50 bg-card/60 p-4 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Price</span>
                        <span className="font-semibold text-green-500">
                          ${(selectedNft.current_price || selectedNft.price || 0).toFixed(2)} USD
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Likes</span>
                        <span className="inline-flex items-center gap-1">
                          <Heart className="w-4 h-4 text-muted-foreground" />
                          {selectedNft.likes_count}
                        </span>
                      </div>
                      {selectedNft.is_listed === false && (
                        <Badge variant="secondary" className="w-full justify-center">
                          Not For Sale
                        </Badge>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <Link to="/marketplace" className="w-full">
                        <Button variant="hero" className="w-full gap-2">
                          <ExternalLink className="w-4 h-4" />
                          Open in Marketplace
                        </Button>
                      </Link>
                      <Button variant="outline" className="w-full" onClick={() => setSelectedNft(null)}>
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>

      <Footer />
    </div>
  );
}

function StatCard({ icon, label, value, gradient }: { 
  icon: React.ReactNode; 
  label: string; 
  value: string | number; 
  gradient: string;
}) {
  return (
    <Card className={`relative overflow-hidden bg-gradient-to-br ${gradient} border-white/10`}>
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
      <CardContent className="relative p-4 md:p-6">
        <div className="flex items-center gap-3">
          <div className="text-primary">{icon}</div>
          <div>
            <p className="text-2xl md:text-3xl font-bold text-foreground">{value}</p>
            <p className="text-xs md:text-sm text-muted-foreground">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProfileNFTCard({ nft, onOpen }: { nft: NFTData; onOpen: () => void }) {
  const displayPrice = nft.current_price || nft.price || 0;
  const isHolding = nft.is_listed === false;
  
  // Use real-time EMON price conversion - same as NFTCard
  const { usdToEmon } = useEmonRate();
  const usdPrice = priceToUsd(displayPrice);
  const emonPrice = usdToEmon(usdPrice);
  
  const formatUsd = (v: number) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);

  return (
    <button type="button" onClick={onOpen} className="w-full text-left">
      <Card className="group relative overflow-hidden bg-card/50 hover:bg-card/80 border-white/10 hover:border-primary/30 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/10">
        <div className="aspect-square overflow-hidden bg-muted">
          {nft.image_url ? (
            <img 
              src={nft.image_url} 
              alt={`${nft.name} NFT image`}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Image className="w-12 h-12 text-muted-foreground/30" />
            </div>
          )}
        </div>
        
        <CardContent className="p-3 space-y-2">
          <h3 className="font-semibold text-foreground truncate">{nft.name}</h3>
          
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-400 font-bold flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                {formatUsd(usdPrice)}
              </span>
              <div className="flex items-center gap-1 text-muted-foreground">
                <Heart className="w-3 h-3" />
                {nft.likes_count}
              </div>
            </div>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Coins className="w-2.5 h-2.5 text-primary" />
              ≈ {emonPrice.toLocaleString()} EMON
            </span>
          </div>

          {isHolding && (
            <Badge variant="secondary" className="w-full justify-center text-xs">
              Not For Sale
            </Badge>
          )}
        </CardContent>

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-4">
          <Badge variant="secondary" className="gap-1">
            <ExternalLink className="w-3 h-3" />
            Open
          </Badge>
        </div>
      </Card>
    </button>
  );
}